import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ColorModeProvider } from './contexts/ColorModeContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import MainLayout from './componentes/MainLayout';
import LoadingFallback from './componentes/comuns/LoadingFallback';
import ErrorBoundary from './componentes/comuns/ErrorBoundary';
import { variedades } from './services/cadastros';
import { getTelaInicial, isAuthenticated, temPermissao } from './services/auth';
import { carregarFuncionalidades } from './services/configuracao';
import { PERMISSOES_PARAMETROS } from './componentes/parametros/secoes';
import { rotaDisponivel, primeiraTelaDisponivel, localizarRota, itemVisivel } from './utils/modulos';

// Lazy loading: carrega apenas quando rota é acessada
const Login = lazy(() => import('./componentes/login/Login'));
const Novidades = lazy(() => import('./componentes/novidades/Novidades'));
const IntegraHubEventos = lazy(() => import('./componentes/integrahub/IntegraHubEventos'));
const IntegraHubMapa = lazy(() => import('./componentes/integrahub/IntegraHubMapa'));
const IntegraHubAdmin = lazy(() => import('./componentes/integrahub/IntegraHubAdmin'));
const PatioAoVivo = lazy(() => import('./componentes/integrahub/PatioAoVivo'));
const Rotinas = lazy(() => import('./componentes/rotinas/Rotinas'));
const Dashboard = lazy(() => import('./componentes/dashboard/Dashboard'));
const Fila = lazy(() => import('./componentes/fila/Fila'));
const NovaEntrada = lazy(() => import('./componentes/fila/NovaEntrada'));
const Chamada = lazy(() => import('./componentes/fila/Chamada'));
const Entradas = lazy(() => import('./componentes/fila/Entradas'));
const Movimentacoes = lazy(() => import('./componentes/fila/Movimentacoes'));
const Painel = lazy(() => import('./componentes/painel/Painel'));
const FilaCarga = lazy(() => import('./componentes/carga/FilaCarga'));
const NovaEntradaCarga = lazy(() => import('./componentes/carga/NovaEntradaCarga'));
const Checklists = lazy(() => import('./componentes/carga/Checklists'));
const Romaneios = lazy(() => import('./componentes/carga/Romaneios'));
const RomaneiosAgendados = lazy(() => import('./componentes/carga/RomaneiosAgendados'));
const RomaneiosAvulsos = lazy(() => import('./componentes/carga/RomaneiosAvulsos'));
const TransportadoraPendente = lazy(() => import('./componentes/carga/TransportadoraPendente'));
const ChamadaCarga = lazy(() => import('./componentes/carga/ChamadaCarga'));
const Motoristas = lazy(() => import('./componentes/cadastros/Motoristas'));
const MotoristaForm = lazy(() => import('./componentes/cadastros/MotoristaForm'));
const Transportadoras = lazy(() => import('./componentes/cadastros/Transportadoras'));
const VinculosPlaca = lazy(() => import('./componentes/cadastros/VinculosPlaca'));
const TransportadoraForm = lazy(() => import('./componentes/cadastros/TransportadoraForm'));
const Impressoras = lazy(() => import('./componentes/cadastros/Impressoras'));
const ImpressoraForm = lazy(() => import('./componentes/cadastros/ImpressoraForm'));
const Produtos = lazy(() => import('./componentes/cadastros/Produtos'));
const ProdutoForm = lazy(() => import('./componentes/cadastros/ProdutoForm'));
const CadastroSimples = lazy(() => import('./componentes/cadastros/CadastroSimples'));
const CadastroSimplesForm = lazy(() => import('./componentes/cadastros/CadastroSimplesForm'));
const Relatorios = lazy(() => import('./componentes/relatorios/Relatorios'));
const MetricasUsuario = lazy(() => import('./componentes/metricas/MetricasUsuario'));
const CustomReportRun = lazy(() => import('./componentes/relatorios/CustomReportRun'));
const Usuarios = lazy(() => import('./componentes/usuarios/Usuarios'));
const UsuarioForm = lazy(() => import('./componentes/usuarios/UsuarioForm'));
const Perfis = lazy(() => import('./componentes/perfis/Perfis'));
const PerfilForm = lazy(() => import('./componentes/perfis/PerfilForm'));
const Auditoria = lazy(() => import('./componentes/auditoria/Auditoria'));
const LogExportacoes = lazy(() => import('./componentes/auditoria/LogExportacoes'));
const LogDownloadsBackup = lazy(() => import('./componentes/auditoria/LogDownloadsBackup'));
const LogDownloadsInstalador = lazy(() => import('./componentes/auditoria/LogDownloadsInstalador'));
const Instaladores = lazy(() => import('./componentes/instaladores/Instaladores'));
const Parametros = lazy(() => import('./componentes/parametros/Parametros'));
const Support = lazy(() => import('./componentes/support/Support'));
const SupportFuncionalidades = lazy(() => import('./componentes/support/Funcionalidades'));
const SupportLayoutRelatorio = lazy(() => import('./componentes/support/LayoutRelatorio'));
const SupportIntegracoes = lazy(() => import('./componentes/support/Integracoes'));
const SupportInteligenciaArtificial = lazy(() => import('./componentes/support/InteligenciaArtificial'));
const SupportDispositivos = lazy(() => import('./componentes/support/Dispositivos'));
const SupportDispositivoDetalhe = lazy(() => import('./componentes/support/DispositivoDetalhe'));
const Dispositivos = lazy(() => import('./componentes/dispositivos/Dispositivos'));
const CustomReportsAdmin = lazy(() => import('./componentes/support/CustomReportsAdmin'));
const CustomReportEditor = lazy(() => import('./componentes/support/CustomReportEditor'));
const CustomReportHistory = lazy(() => import('./componentes/support/CustomReportHistory'));
const Pesagens = lazy(() => import('./componentes/pesagem/Pesagens'));
const AtendimentoAssist = lazy(() => import('./componentes/assist/AtendimentoAssist'));
const RespostasProntasAssist = lazy(() => import('./componentes/assist/RespostasProntasAssist'));
const Balancas = lazy(() => import('./componentes/pesagem/Balancas'));
const BalancaForm = lazy(() => import('./componentes/pesagem/BalancaForm'));
const ChavesApi = lazy(() => import('./componentes/integracao/ChavesApi'));

const RotaProtegida = ({ children }) => {
    if (!isAuthenticated()) return <Navigate to="/login" replace />;
    // Flags da instalação atualizadas em segundo plano. A navegação é montada de
    // forma síncrona a partir do cache em localStorage: quem acabou de logar vê
    // o menu do login anterior por um instante, e a resposta corrige na
    // sequência — melhor do que segurar a área autenticada inteira esperando.
    carregarFuncionalidades();
    return children;
};

const RotaPublica = ({ children }) => {
    // destinoSeguro e nao getTelaInicial: quem acabou de logar num perfil cuja
    // tela inicial pertence a modulo desligado cairia numa rota indisponivel.
    if (isAuthenticated()) return <Navigate to={destinoSeguro('/login')} replace />;
    return children;
};

// Guard por permissão do perfil. Sem a permissão, volta pra tela inicial do
// perfil; se a própria tela inicial estiver mal configurada (sem permissão),
// evita loop de redirecionamento indo pro login.
const RotaComPermissao = ({ permissao, children }) => {
    const location = useLocation();
    if (!isAuthenticated()) return <Navigate to="/login" replace />;

    // Funcionalidade antes de permissão: com o módulo desligado, o back-end
    // responde 422 em toda chamada, e ter permissão não muda isso. Esconder o
    // item do menu não basta — URL digitada, link salvo e histórico chegam aqui.
    if (!rotaDisponivel(location.pathname)) return <Navigate to={destinoSeguro(location.pathname)} replace />;

    // Lista de permissões: basta uma. Existe para a central de parâmetros, que
    // reúne seções de regras diferentes — a seção em si é conferida dentro.
    const permitido = Array.isArray(permissao)
        ? permissao.some(temPermissao)
        : (!permissao || temPermissao(permissao));
    if (!permitido) {
        return <Navigate to={destinoSeguro(location.pathname)} replace />;
    }
    return children;
};

// Rota antiga que virou seção da central de parâmetros. Mantém a busca da URL:
// link com filtro salvo continua chegando com ela.
const RedirecionaParaSecao = ({ secao }) => {
    const { search } = useLocation();
    return <Navigate to={`/parametros/${secao}${search}`} replace />;
};

// Para onde mandar quem não pode ficar onde está.
//
// A tela inicial do perfil pode pertencer a módulo desligado — todo módulo é
// desligável, inclusive o Início. Mandar para lá cegamente devolveria o usuário
// a uma rota indisponível, que o mandaria de volta: laço de redirecionamento.
//
// Por isso a ordem: tela inicial se ela própria estiver disponível; senão a
// primeira tela que o perfil consegue abrir; e só no fim o login, quando não
// sobrou nada — instalação com tudo desligado é configuração errada, e o
// usuário precisa ver isso em vez de rodar em círculo.
const destinoSeguro = (origem) => {
    const telaInicial = getTelaInicial();
    if (telaInicial !== origem && rotaDisponivel(telaInicial) && temPermissaoDaRota(telaInicial)) {
        return telaInicial;
    }
    const alternativa = primeiraTelaDisponivel();
    if (alternativa && alternativa !== origem) return alternativa;
    return '/login';
};

// Permissão da tela, lida do próprio catálogo de módulos.
const temPermissaoDaRota = (rota) => {
    const { item } = localizarRota(rota) || {};
    if (!item) return true;
    return itemVisivel(item);
};

function App() {
    return (
        <ColorModeProvider>
            <SnackbarProvider>
                <BrowserRouter>
                    <Rotas />
                </BrowserRouter>
            </SnackbarProvider>
        </ColorModeProvider>
    );
}

// Isolado do App() porque useLocation() só funciona dentro do BrowserRouter.
// O key={pathname} no ErrorBoundary é o que faz a tela seguinte tentar de novo
// do zero em vez de ficar presa na mensagem de erro da tela anterior — e, de
// quebra, garante que cada rota do MPG Support monte sua própria instância em
// vez de reaproveitar estado (dados, filtros) da tela anterior.
function Rotas() {
    const { pathname } = useLocation();
    return (
        <ErrorBoundary key={pathname}>
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    <Route path="/login" element={<RotaPublica><Login /></RotaPublica>} />

                    {/* Painel de chamada (modo quiosque) — acesso público, sem menus */}
                    <Route path="/painel" element={<Painel />} />

                    {/* Tela simplificada de chamada para operadores — autenticada, sem side-nav */}
                    <Route
                        path="/chamada"
                        element={<RotaComPermissao permissao="FILA_DESCARGA_OPERAR"><Chamada /></RotaComPermissao>}
                    />
                    <Route
                        path="/chamada-carga"
                        element={<RotaComPermissao permissao="FILA_CARGA_OPERAR"><ChamadaCarga /></RotaComPermissao>}
                    />

                    <Route path="/" element={<RotaProtegida><MainLayout /></RotaProtegida>}>
                        <Route path="novidades" element={<Novidades />} />
                        <Route index element={<Navigate to={destinoSeguro('/')} replace />} />
                        <Route
                            path="/dashboard"
                            element={<RotaComPermissao permissao="DASHBOARD_VISUALIZAR"><Dashboard /></RotaComPermissao>}
                        />
                        <Route
                            path="/integrahub/eventos"
                            element={<RotaComPermissao permissao="INTEGRAHUB_VISUALIZAR"><IntegraHubEventos /></RotaComPermissao>}
                        />
                        <Route path="/patio/mapa" element={<RotaComPermissao permissao="INTEGRAHUB_VISUALIZAR"><PatioAoVivo /></RotaComPermissao>} />
                        <Route path="/integrahub/mapa" element={<RotaComPermissao permissao="INTEGRAHUB_VISUALIZAR"><IntegraHubMapa /></RotaComPermissao>} />
                        <Route path="/rotinas" element={<RotaComPermissao permissao="ROTINAS_VISUALIZAR"><Rotinas /></RotaComPermissao>} />
                        <Route path="/integrahub/configuracao" element={<RotaComPermissao permissao="INTEGRAHUB_ADMINISTRAR"><IntegraHubAdmin /></RotaComPermissao>} />
                        <Route
                            path="/fila"
                            element={<RotaComPermissao permissao="FILA_DESCARGA_VISUALIZAR"><Fila /></RotaComPermissao>}
                        />
                        <Route
                            path="/fila/nova"
                            element={<RotaComPermissao permissao="FILA_DESCARGA_OPERAR"><NovaEntrada /></RotaComPermissao>}
                        />
                        <Route
                            path="/entradas"
                            element={<RotaComPermissao permissao="FILA_DESCARGA_VISUALIZAR"><Entradas tipo="DESCARGA" /></RotaComPermissao>}
                        />
                        <Route
                            path="/movimentacoes"
                            element={<RotaComPermissao permissao="FILA_DESCARGA_VISUALIZAR"><Movimentacoes tipo="DESCARGA" /></RotaComPermissao>}
                        />
                        {/* Mesmas telas servindo a fila de carga, com a permissão da carga */}
                        <Route
                            path="/entradas-carga"
                            element={<RotaComPermissao permissao="FILA_CARGA_VISUALIZAR"><Entradas tipo="CARGA" /></RotaComPermissao>}
                        />
                        <Route
                            path="/movimentacoes-carga"
                            element={<RotaComPermissao permissao="FILA_CARGA_VISUALIZAR"><Movimentacoes tipo="CARGA" /></RotaComPermissao>}
                        />
                        <Route
                            path="/fila-carga"
                            element={<RotaComPermissao permissao="FILA_CARGA_VISUALIZAR"><FilaCarga /></RotaComPermissao>}
                        />
                        <Route
                            path="/fila-carga/nova"
                            element={<RotaComPermissao permissao="FILA_CARGA_REGISTRAR_ENTRADA"><NovaEntradaCarga /></RotaComPermissao>}
                        />
                        <Route
                            path="/checklists"
                            element={<RotaComPermissao permissao="CHECKLIST_CONSULTAR"><Checklists /></RotaComPermissao>}
                        />
                        {/* O que a Portaria deixou em aberto quando a instalação
                            aceita chegada sem transportadora (issue #422). Visualizar
                            basta para acompanhar; vincular exige permissão própria. */}
                        <Route
                            path="/transportadora-pendente"
                            element={<RotaComPermissao permissao="FILA_CARGA_VISUALIZAR"><TransportadoraPendente /></RotaComPermissao>}
                        />
                        {/* Quarto passo do fluxo de carga: registra entrada, Guarita
                            inspeciona, Vendas aprova e informa o romaneio aqui. */}
                        <Route
                            path="/romaneios"
                            element={<RotaComPermissao permissao="PROCESSO_CARGA_VISUALIZAR"><Romaneios /></RotaComPermissao>}
                        />
                        {/* O que o ERP mandou antes do caminhão chegar (issue #456).
                            Só lista e agenda: o vínculo com a carga é do back, quando
                            a placa entra na fila. */}
                        <Route
                            path="/romaneios-agendados"
                            element={<RotaComPermissao permissao="ROMANEIO_AGENDAR_VISUALIZAR"><RomaneiosAgendados /></RotaComPermissao>}
                        />
                        {/* Romaneio de entrada ou saída sem fila e sem ERP (issue #706). */}
                        <Route
                            path="/romaneios-avulsos"
                            element={<RotaComPermissao permissao="ROMANEIO_AVULSO_VISUALIZAR"><RomaneiosAvulsos /></RotaComPermissao>}
                        />
                        {/* Tela inicial do perfil Vendas: mesma consulta/aprovação de checklists */}
                        <Route
                            path="/analise-checklists"
                            element={<RotaComPermissao permissao="CHECKLIST_CONSULTAR"><Checklists /></RotaComPermissao>}
                        />
                        <Route
                            path="/motoristas"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><Motoristas /></RotaComPermissao>}
                        />
                        <Route
                            path="/motoristas/novo"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><MotoristaForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/motoristas/:id/editar"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><MotoristaForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/variedades"
                            element={(
                                <RotaComPermissao permissao="CADASTROS_GERENCIAR">
                                    <CadastroSimples key="variedades" titulo="Variedades" servico={variedades} rotaBase="/variedades" />
                                </RotaComPermissao>
                            )}
                        />
                        <Route
                            path="/variedades/novo"
                            element={(
                                <RotaComPermissao permissao="CADASTROS_GERENCIAR">
                                    <CadastroSimplesForm titulo="Variedades" servico={variedades} rotaBase="/variedades" />
                                </RotaComPermissao>
                            )}
                        />
                        <Route
                            path="/variedades/:id/editar"
                            element={(
                                <RotaComPermissao permissao="CADASTROS_GERENCIAR">
                                    <CadastroSimplesForm titulo="Variedades" servico={variedades} rotaBase="/variedades" />
                                </RotaComPermissao>
                            )}
                        />
                        <Route
                            path="/transportadoras"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><Transportadoras /></RotaComPermissao>}
                        />
                        <Route
                            path="/transportadoras/novo"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><TransportadoraForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/transportadoras/:id/editar"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><TransportadoraForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/vinculos-placa"
                            element={<RotaComPermissao permissao="VINCULO_PLACA_GERENCIAR"><VinculosPlaca /></RotaComPermissao>}
                        />
                        {/* Módulo de pesagem — opcional por instalação
                            (app.funcionalidades.pesagem). As rotas ficam
                            sempre registradas; com a flag desligada o
                            módulo some do menu e o back não expõe a API. */}
                        <Route
                            path="/pesagem"
                            element={<RotaComPermissao permissao="PESAGEM_VISUALIZAR"><Pesagens /></RotaComPermissao>}
                        />
                        <Route
                            path="/balancas"
                            element={<RotaComPermissao permissao="PESAGEM_CONFIGURAR"><Balancas /></RotaComPermissao>}
                        />
                        <Route
                            path="/balancas/novo"
                            element={<RotaComPermissao permissao="PESAGEM_CONFIGURAR"><BalancaForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/balancas/:id/editar"
                            element={<RotaComPermissao permissao="PESAGEM_CONFIGURAR"><BalancaForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/chaves-api"
                            element={<RotaComPermissao permissao="INTEGRACAO_GERENCIAR"><ChavesApi /></RotaComPermissao>}
                        />
                        <Route
                            path="/produtos"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><Produtos /></RotaComPermissao>}
                        />
                        <Route
                            path="/produtos/novo"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><ProdutoForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/produtos/:id/editar"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><ProdutoForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/impressoras"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><Impressoras /></RotaComPermissao>}
                        />
                        <Route
                            path="/impressoras/novo"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><ImpressoraForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/impressoras/:id/editar"
                            element={<RotaComPermissao permissao="CADASTROS_GERENCIAR"><ImpressoraForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/relatorios"
                            element={<RotaComPermissao permissao="RELATORIOS_VISUALIZAR"><Relatorios /></RotaComPermissao>}
                        />
                        <Route
                            path="/metricas"
                            element={<RotaComPermissao permissao={['METRICAS_PROPRIAS_VISUALIZAR', 'METRICAS_EQUIPE_VISUALIZAR']}><MetricasUsuario /></RotaComPermissao>}
                        />
                        <Route
                            path="/relatorios/personalizados/:slug"
                            element={<RotaComPermissao permissao="RELATORIOS_VISUALIZAR"><CustomReportRun /></RotaComPermissao>}
                        />
                        <Route
                            path="/usuarios"
                            element={<RotaComPermissao permissao="USUARIOS_GERENCIAR"><Usuarios /></RotaComPermissao>}
                        />
                        <Route
                            path="/usuarios/novo"
                            element={<RotaComPermissao permissao="USUARIOS_GERENCIAR"><UsuarioForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/usuarios/:id/editar"
                            element={<RotaComPermissao permissao="USUARIOS_GERENCIAR"><UsuarioForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/perfis"
                            element={<RotaComPermissao permissao="PERFIS_GERENCIAR"><Perfis /></RotaComPermissao>}
                        />
                        <Route
                            path="/perfis/novo"
                            element={<RotaComPermissao permissao="PERFIS_GERENCIAR"><PerfilForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/perfis/:id/editar"
                            element={<RotaComPermissao permissao="PERFIS_GERENCIAR"><PerfilForm /></RotaComPermissao>}
                        />
                        <Route
                            path="/auditoria"
                            element={<RotaComPermissao permissao="AUDITORIA_VISUALIZAR"><Auditoria /></RotaComPermissao>}
                        />
                        <Route
                            path="/exportacoes"
                            element={<RotaComPermissao permissao="AUDITORIA_VISUALIZAR"><LogExportacoes /></RotaComPermissao>}
                        />
                        <Route
                            path="/downloads-backup"
                            element={<RotaComPermissao permissao="AUDITORIA_VISUALIZAR"><LogDownloadsBackup /></RotaComPermissao>}
                        />
                        <Route
                            path="/downloads-instalador"
                            element={<RotaComPermissao permissao="AUDITORIA_VISUALIZAR"><LogDownloadsInstalador /></RotaComPermissao>}
                        />
                        <Route
                            path="/instaladores"
                            element={<RotaComPermissao permissao="INSTALADOR_BAIXAR"><Instaladores /></RotaComPermissao>}
                        />
                        <Route
                            path="/assist/atendimento"
                            element={<RotaComPermissao permissao="ASSIST_ATENDER"><AtendimentoAssist /></RotaComPermissao>}
                        />
                        <Route
                            path="/assist/respostas-prontas"
                            element={<RotaComPermissao permissao="ASSIST_ATENDER"><RespostasProntasAssist /></RotaComPermissao>}
                        />
                        {/* Central de parâmetros: a seção vive na URL para que
                            F5, link direto, migalha e voltar/avançar do
                            navegador funcionem. Sem seção, o próprio componente
                            manda para a primeira que o perfil pode abrir.

                            A guarda aceita qualquer permissão que abra ALGUMA
                            seção — quem só tem CANAL_ENTREGA_VISUALIZAR entra
                            para ver o canal de envio. Qual seção ele pode ver
                            é decidido dentro, seção a seção. */}
                        <Route
                            path="/parametros"
                            element={<RotaComPermissao permissao={PERMISSOES_PARAMETROS}><Parametros /></RotaComPermissao>}
                        />
                        <Route
                            path="/parametros/:secao"
                            element={<RotaComPermissao permissao={PERMISSOES_PARAMETROS}><Parametros /></RotaComPermissao>}
                        />
                        {/* Rotas antigas das telas que viraram seção (issue #424).
                            Não somem: há link salvo, documentação e histórico
                            apontando para elas. A busca da URL é preservada para
                            não quebrar link com parâmetro. */}
                        <Route path="/canal-entrega" element={<RedirecionaParaSecao secao="canal-envio" />} />
                        <Route path="/empresa" element={<RedirecionaParaSecao secao="empresa" />} />
                        {[
                            ['/support', 'MPG_SUPPORT_DIAGNOSTICO'], ['/support/logs', 'MPG_SUPPORT_LOGS'],
                            ['/support/sql', 'MPG_SUPPORT_SQL'], ['/support/database', 'MPG_SUPPORT_DATABASE'],
                            ['/support/diagnostico', 'MPG_SUPPORT_DIAGNOSTICO'], ['/support/fila', 'MPG_SUPPORT_DIAGNOSTICO'],
                            ['/support/trace', 'MPG_SUPPORT_LOGS'], ['/support/sessoes', 'MPG_SUPPORT_DIAGNOSTICO'],
                            ['/support/ferramentas', 'MPG_SUPPORT_REPARO'],
                        ].map(([path, permissao]) => (
                            <Route key={path} path={path} element={<RotaComPermissao permissao={permissao}><Support /></RotaComPermissao>} />
                        ))}
                        {/* Componente próprio, fora do Support.jsx: esta é a única rota
                            de suporte que escreve. MPG_SUPPORT_ACESSAR abre a leitura;
                            o botão de alterar exige MPG_SUPPORT_FUNCIONALIDADES, checada
                            no componente e de novo no backend. */}
                        <Route
                            path="/support/funcionalidades"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_ACESSAR"><SupportFuncionalidades /></RotaComPermissao>}
                        />
                        {/* Mesmo desenho: ver o layout é parte de diagnosticar um chamado
                            de "relatório saiu torto", então a rota pede só o acesso ao
                            módulo; salvar e gerar prévia exigem
                            MPG_SUPPORT_LAYOUT_RELATORIO, no componente e no backend. */}
                        <Route
                            path="/support/layout-relatorio"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_ACESSAR"><SupportLayoutRelatorio /></RotaComPermissao>}
                        />
                        {/* Ver a configuração da integração é parte de diagnosticar
                            "a importação parou": a rota pede só o acesso ao módulo, e
                            cadastrar/testar exige INTEGRACAO_CONFIGURAR, checada no
                            componente e de novo no backend. */}
                        <Route
                            path="/support/integracoes"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_ACESSAR"><SupportIntegracoes /></RotaComPermissao>}
                        />
                        {/* Diferente das vizinhas: aqui até ver exige a permissão
                            própria, porque a tela mostra de onde vem a chave e
                            quem a trocou — e só quem pode trocar precisa disso (#572). */}
                        <Route
                            path="/support/ia"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_IA"><SupportInteligenciaArtificial /></RotaComPermissao>}
                        />
                        <Route
                            path="/support/dispositivos"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_ACESSAR"><SupportDispositivos /></RotaComPermissao>}
                        />
                        <Route
                            path="/support/dispositivos/:id"
                            element={<RotaComPermissao permissao="MPG_SUPPORT_ACESSAR"><SupportDispositivoDetalhe /></RotaComPermissao>}
                        />
                        {/* Visão do cliente. Rota curta e permissão
                            própria: não é a tela do suporte com campos
                            escondidos — o backend devolve outro DTO. */}
                        <Route
                            path="/dispositivos"
                            element={<RotaComPermissao permissao="DISPOSITIVOS_VISUALIZAR"><Dispositivos /></RotaComPermissao>}
                        />
                        <Route path="/support/relatorios-personalizados" element={<RotaComPermissao permissao="MPG_SUPPORT_SQL"><CustomReportsAdmin /></RotaComPermissao>} />
                        <Route path="/support/relatorios-personalizados/novo" element={<RotaComPermissao permissao="MPG_SUPPORT_SQL"><CustomReportEditor /></RotaComPermissao>} />
                        <Route path="/support/relatorios-personalizados/:id/editar" element={<RotaComPermissao permissao="MPG_SUPPORT_SQL"><CustomReportEditor /></RotaComPermissao>} />
                        <Route path="/support/relatorios-personalizados/:id/historico" element={<RotaComPermissao permissao="MPG_SUPPORT_SQL"><CustomReportHistory /></RotaComPermissao>} />
                    </Route>

                    <Route path="*" element={<Navigate to={isAuthenticated() ? destinoSeguro('/') : '/login'} replace />} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
}

export default App;
